import {
  LEAD_STATUSES,
  statusMeta,
  type LeadStatus,
  type LeadWithEcon,
} from '@delvero/contracts';
import {
  closestCorners,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  Announcements,
  DragEndEvent,
  DragStartEvent,
  DropAnimation,
  ScreenReaderInstructions,
} from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../components/Icon';
import { fmtZl, relDate } from '../../lib/format';
import { orpc } from '../../lib/orpc';

const DROP_ANIMATION: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.4' } } }),
};

const ANNOUNCEMENTS: Announcements = {
  onDragStart: ({ active }) => `Podniesiono lead ${active.data.current?.card?.company ?? active.id}.`,
  onDragOver: ({ over }) =>
    over ? `Nad kolumną ${statusMeta(over.id as LeadStatus).label}.` : 'Poza kolumnami.',
  onDragEnd: ({ over }) =>
    over
      ? `Upuszczono w kolumnie ${statusMeta(over.id as LeadStatus).label}.`
      : 'Upuszczono poza kolumnami — bez zmiany.',
  onDragCancel: () => 'Anulowano przeciąganie.',
};

const SR_INSTRUCTIONS: ScreenReaderInstructions = {
  draggable:
    'Aby podnieść lead, naciśnij spację lub enter na uchwycie. Strzałkami przenieś między kolumnami. Naciśnij spację lub enter, aby upuścić, albo escape, aby anulować.',
};

/** Optimistically move a lead between status columns, reconciling on settle. */
function useMoveLeadStatus(appId: string) {
  const queryClient = useQueryClient();
  const listKey = orpc.leads.list.queryOptions({ input: { appId } }).queryKey;

  type MoveCtx = { previous: LeadWithEcon[] | undefined };

  return useMutation(
    orpc.leads.update.mutationOptions<MoveCtx>({
      onMutate: async ({ id, patch }): Promise<MoveCtx> => {
        await queryClient.cancelQueries({ queryKey: listKey });
        const previous = queryClient.getQueryData<LeadWithEcon[]>(listKey);
        if (patch.status !== undefined) {
          const nextStatus = patch.status;
          queryClient.setQueryData<LeadWithEcon[]>(listKey, (old) =>
            old?.map((l) => (l.id === id ? { ...l, status: nextStatus } : l)),
          );
        }
        return { previous };
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) queryClient.setQueryData(listKey, context.previous);
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.leads.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.dashboard.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.costs.key() });
      },
    }),
  );
}

function LeadCardInner({ lead }: { lead: LeadWithEcon }) {
  return (
    <>
      <div className="lead-card-row">
        <span className="cell-main">{lead.company}</span>
        {lead.econ.mrr > 0 ? <span className="lead-card-mrr">{fmtZl(lead.econ.mrr)}</span> : null}
      </div>
      <span className="cell-sub">
        {lead.contact ? `${lead.contact}${lead.role ? ` · ${lead.role}` : ''}` : 'Brak kontaktu'}
      </span>
      <div className="lead-card-row">
        <span className="cell-sub">{lead.planName ?? '—'}</span>
        <span className="cell-sub">{relDate(lead.lastActivityAt)}</span>
      </div>
    </>
  );
}

function LeadCard({
  lead,
  color,
  onOpen,
}: {
  lead: LeadWithEcon;
  color: string;
  onOpen: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: lead.id,
    data: { type: 'card', card: lead, fromStatus: lead.status },
  });

  return (
    <li
      ref={setNodeRef}
      className="lead-card"
      style={{ borderLeftColor: color, opacity: isDragging ? 0.4 : 1 }}
    >
      <span
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        className="lead-card-grip"
        aria-label={`Przeciągnij, by zmienić status — ${lead.company}`}
      >
        <Icon name="grip" size={14} />
      </span>
      <button type="button" className="lead-card-body" onClick={() => onOpen(lead.id)}>
        <LeadCardInner lead={lead} />
      </button>
    </li>
  );
}

function BoardColumn({
  status,
  label,
  color,
  leads,
  onOpen,
}: {
  status: LeadStatus;
  label: string;
  color: string;
  leads: LeadWithEcon[];
  onOpen: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status, data: { type: 'column', status } });

  return (
    <section ref={setNodeRef} className={`board-col${isOver ? ' is-over' : ''}`} aria-label={label}>
      <header className="board-col-head">
        <span className="dot" style={{ background: color }} />
        <span className="board-col-title">{label}</span>
        <span className="cnt mono">{leads.length}</span>
      </header>
      <ul className="board-col-body">
        {leads.map((l) => (
          <LeadCard key={l.id} lead={l} color={color} onOpen={onOpen} />
        ))}
        {leads.length === 0 ? <li className="board-empty">Upuść tutaj</li> : null}
      </ul>
    </section>
  );
}

function LeadCardOverlay({ lead }: { lead: LeadWithEcon }) {
  return (
    <div
      className="lead-card lead-card-overlay"
      style={{ borderLeftColor: statusMeta(lead.status).color }}
    >
      <span className="lead-card-grip">
        <Icon name="grip" size={14} />
      </span>
      <div className="lead-card-body">
        <LeadCardInner lead={lead} />
      </div>
    </div>
  );
}

export function PipelineBoard({
  leads,
  appId,
  onOpen,
}: {
  leads: LeadWithEcon[];
  appId: string;
  onOpen: (id: string) => void;
}) {
  const moveLead = useMoveLeadStatus(appId);
  const [activeCard, setActiveCard] = useState<LeadWithEcon | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const byStatus = useMemo(() => {
    const map = new Map<LeadStatus, LeadWithEcon[]>();
    for (const s of LEAD_STATUSES) map.set(s.id, []);
    for (const l of leads) map.get(l.status)?.push(l);
    return map;
  }, [leads]);

  function handleDragEnd(event: DragEndEvent) {
    setActiveCard(null);
    const { active, over } = event;
    if (!over) return;
    const fromStatus = active.data.current?.fromStatus as LeadStatus | undefined;
    const toStatus = over.id as LeadStatus;
    if (!fromStatus || fromStatus === toStatus) return;
    moveLead.mutate({ id: String(active.id), patch: { status: toStatus } });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      accessibility={{ announcements: ANNOUNCEMENTS, screenReaderInstructions: SR_INSTRUCTIONS }}
      onDragStart={(e: DragStartEvent) =>
        setActiveCard((e.active.data.current?.card as LeadWithEcon) ?? null)
      }
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveCard(null)}
    >
      {moveLead.isError ? (
        <div className="board-move-error" role="status" aria-live="polite">
          Nie udało się zmienić statusu — przywrócono poprzedni.
        </div>
      ) : null}
      <div className="board">
        {LEAD_STATUSES.map((s) => (
          <BoardColumn
            key={s.id}
            status={s.id}
            label={s.label}
            color={s.color}
            leads={byStatus.get(s.id) ?? []}
            onOpen={onOpen}
          />
        ))}
      </div>
      {createPortal(
        <DragOverlay dropAnimation={DROP_ANIMATION}>
          {activeCard ? <LeadCardOverlay lead={activeCard} /> : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
